const VIMEO_API_URL = 'https://player.vimeo.com/api/player.js';

export function buildVimeoUrl(id, loop) {
  const params = new URLSearchParams({
    autoplay: '1',
    muted: '1',
    controls: '0',
    loop: loop ? '1' : '0',
    autopause: '0',
    dnt: '1',
    playsinline: '1',
  });
  return `https://player.vimeo.com/video/${id}?${params}`;
}

export function shouldCoverIntro(seconds, duration) {
  return duration > 0 && duration - seconds <= 0.6;
}

let vimeoApiPromise;

function loadVimeoApi() {
  if (window.Vimeo?.Player) return Promise.resolve(window.Vimeo);
  if (vimeoApiPromise) return vimeoApiPromise;

  vimeoApiPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${VIMEO_API_URL}"]`);
    const script = existing || Object.assign(document.createElement('script'), { src: VIMEO_API_URL });
    script.addEventListener('load', () => resolve(window.Vimeo), { once: true });
    script.addEventListener('error', reject, { once: true });
    if (!existing) document.head.append(script);
  });

  return vimeoApiPromise;
}

const HTMLElementBase = globalThis.HTMLElement || class {};

class VimeoBackground extends HTMLElementBase {
  connectedCallback() {
    this.frame = this.querySelector('[data-vimeo-frame]');
    this.poster = this.querySelector('[data-vimeo-poster]');
    this.videoId = this.dataset.ypyVimeoId;
    if (matchMedia('(max-width: 1023px)').matches && this.dataset.ypyVimeoMobileId) {
      this.videoId = this.dataset.ypyVimeoMobileId;
    }
    this.introId = this.dataset.ypyVimeoIntroId;
    this.activeId = this.introId || this.videoId;
    this.aspectRatio = 16 / 9;
    this.isPaused = false;

    this.frame.src = buildVimeoUrl(this.activeId, !this.introId);
    this.resizeObserver = new ResizeObserver(() => this.resizeFrame());
    this.resizeObserver.observe(this);
    this.playerPromise = this.connectPlayer();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  async connectPlayer() {
    const Vimeo = await loadVimeoApi();
    this.player = new Vimeo.Player(this.frame);
    await this.player.ready();
    await this.updateAspectRatio();

    this.player.on('play', () => {
      this.isPaused = false;
      this.poster?.classList.remove('is-transitioning');
      this.poster?.classList.add('is-hidden');
    });
    this.player.on('pause', () => {
      this.isPaused = true;
    });

    if (this.introId) {
      const coverIntroEnd = ({ seconds, duration }) => {
        if (!this.introId || !shouldCoverIntro(seconds, duration)) return;
        this.poster?.classList.add('is-transitioning');
        this.poster?.classList.remove('is-hidden');
        this.player.off('timeupdate', coverIntroEnd);
      };
      this.player.on('timeupdate', coverIntroEnd);

      this.player.on('ended', async () => {
        if (!this.introId) return;
        this.introId = '';
        await this.player.loadVideo(this.videoId);
        await this.player.setLoop(true);
        await this.updateAspectRatio();
        await this.player.play();
      });
    }
  }

  async updateAspectRatio() {
    const [width, height] = await Promise.all([this.player.getVideoWidth(), this.player.getVideoHeight()]);
    if (width && height) this.aspectRatio = width / height;
    this.resizeFrame();
  }

  resizeFrame() {
    if (!this.frame) return;
    const { width, height } = this.getBoundingClientRect();
    if (!width || !height) return;

    if (width / height > this.aspectRatio) {
      this.frame.style.width = `${width}px`;
      this.frame.style.height = `${width / this.aspectRatio}px`;
    } else {
      this.frame.style.width = `${height * this.aspectRatio}px`;
      this.frame.style.height = `${height}px`;
    }
  }

  async play() {
    await this.playerPromise;
    await this.player.play();
  }

  async pause() {
    await this.playerPromise;
    await this.player.pause();
  }

  get paused() {
    return this.isPaused;
  }
}

if (globalThis.customElements && !customElements.get('vimeo-background')) {
  customElements.define('vimeo-background', VimeoBackground);
}
